import SceneSprites, {EVENT_LOADED as EVENT_SPRITES_LOADED} from "./SceneSprites";
import EventDispatcher from "ui/EventDispatcher";
import CanvasClipArea from "ui/canvas/CanvasClipArea";
import {SCENE_FINAL_WIDTH, SCENE_GRID_INDENT, SCENE_SOURCE_WIDTH} from "ui/config";

/**
 * @type {string}
 */
export const EVENT_CHANGED = 'changed';

/**
 * Neighbor Scenes Model
 */
export default class SceneNeighbors extends EventDispatcher {
	/**
	 * Class constructor
	 */
	constructor() {
		super();

		/**
		 * @type {Array<T_SceneNeighborSource>}
		 * @private
		 */
		this._sources = [];

		/**
		 * @type {Map<string, T_SceneBoundary>}
		 * @private
		 */
		this._boundaries = new Map();

		/**
		 * @type {Map<string, Array<T_SceneNeighborPosition>>}
		 * @private
		 */
		this._cache = new Map();

		this._handlers = {
			onChange: this._triggerChange.bind(this)
		};
	}

	/**
	 * Sets neighbor scenes data
	 *
	 * @param {Array<T_SceneNeighborInput>} data
	 * @returns {Promise<void>}
	 */
	async setData(data) {
		this._cache.clear();
		this._sources = [];
		let wait = [];
		for (let info of data) {
			let sprites;
			if (info.url === '') {
				sprites = null;
			} else {
				sprites = new SceneSprites();
				sprites.addListener(EVENT_SPRITES_LOADED, this._handlers.onChange);
				wait.push(sprites.loadImage(info.url, info.index, info.frames_url || ''));
			}
			this._sources.push({id: info.id, sprites});
		}
		await Promise.all(wait);
	}

	/**
	 * Calculates and returns list of the neighbor scenes boundaries
	 *
	 * @param {number} sceneWidth
	 * @param {number} sceneHeight
	 * @param {number} [offsetX]
	 * @param {number} [offsetY]
	 * @param {number} [scaleFactor]
	 * @returns {Array<T_SceneNeighborPosition>}
	 */
	getPositions(sceneWidth, sceneHeight, offsetX = 0, offsetY = 0, scaleFactor = 1) {
		if (this._sources.length === 0) {
			// this scene has no neighbors
			return [];
		}
		let size = sceneWidth + '_' + sceneHeight;
		let cacheKey = size + ';' + offsetX + ',' + offsetY + ',' + scaleFactor;
		if (this._cache.has(cacheKey)) {
			return this._cache.get(cacheKey);
		}
		if (!this._boundaries.has(size)) {
			this._boundaries.set(size, new CanvasClipArea(sceneWidth, sceneHeight).getProperties());
		}
		let {wallHeight, floorHeight, floorHalfHeight, halfWidth} = this._boundaries.get(size);

		halfWidth *= scaleFactor;
		floorHeight *= scaleFactor;
		floorHalfHeight *= scaleFactor;
		wallHeight *= scaleFactor;
		let out = [],
			padding = (SCENE_GRID_INDENT + 1) * (sceneWidth / SCENE_SOURCE_WIDTH) * scaleFactor,
			sx = -offsetX * scaleFactor,
			sy = -offsetY * scaleFactor,
			sw = sceneWidth * scaleFactor,
			sh = sceneHeight * scaleFactor;

		const neighborsSizes = [
			[7, false, sx, sy - sh + wallHeight + padding * 2], // top
			[0, false, sx + halfWidth - padding, sy - floorHalfHeight + padding], // top-right
			[6, false, sx - halfWidth + padding, sy - floorHalfHeight + padding], // top-left
			[1, false, sx + sw - padding * 2, sy], // middle-right
			[5, false, sx - sw + padding * 2, sy], // middle-left
			[4, true, sx - halfWidth + padding, sy + floorHalfHeight - padding], // bottom-left
			[2, true, sx + halfWidth - padding, sy + floorHalfHeight - padding], // bottom-right
			[3, true, sx, sy + floorHeight - padding * 2], // bottom

		];
		for (let i = 0; i < neighborsSizes.length; i++) {
			let img = this._sources[neighborsSizes[i][0]];
			out.push({
				id: img ? img.id : '',
				sprites: img ? img.sprites : null,
				x: neighborsSizes[i][2],
				y: neighborsSizes[i][3],
				width: sw,
				height: sh,
				halfWidth,
				floorHeight,
				floorHalfHeight,
				wallHeight,
				isOver: neighborsSizes[i][1]
			});
		}
		this._cache.set(cacheKey, out);
		return out;
	}

	/**
	 * Draws neighbor scenes that under current one
	 *
	 * @param {number} mode 0 - all scenes, 1 - above current one, 2 - under current one
	 * @param {CanvasRenderingContext2D} ctx
	 * @param {number} frame
	 * @param {number} sceneWidth
	 * @param {number} sceneHeight
	 * @param {number} [offsetX]
	 * @param {number} [offsetY]
	 * @param {number} [scaleFactor]
	 */
	drawScenes(mode, ctx, frame, sceneWidth, sceneHeight, offsetX = 0, offsetY = 0, scaleFactor = 1) {
		let list = this.getPositions(sceneWidth, sceneHeight, offsetX, offsetY, scaleFactor);
		if (sceneWidth === SCENE_SOURCE_WIDTH) {
			// we need to make scale bigger, because sprite has final size, but requested draw as source size
			scaleFactor *= SCENE_SOURCE_WIDTH / SCENE_FINAL_WIDTH;
		}

		for (let pos of list) {
			if ((mode === 1 && pos.isOver) || (mode === 2 && !pos.isOver)) {
				continue;
			}
			if (pos.sprites === null) {
				this._renderBlankScene(ctx, pos, scaleFactor);
				continue;
			}
			pos.sprites.drawFrame(ctx, frame, pos.x, pos.y, scaleFactor);
		}
	}

	/**
	 * Draws blank image for neighbor scene
	 *
	 * @param {CanvasRenderingContext2D} ctx
	 * @param {T_SceneNeighborPosition} pos
	 * @param {number} scaleFactor
	 * @returns {SceneNeighbors}
	 * @private
	 */
	_renderBlankScene(ctx, pos, scaleFactor) {
		let padding = 5 * scaleFactor;
		ctx.lineWidth = 0.5;
		ctx.strokeStyle = 'rgba(200, 200, 200, 1)';
		ctx.beginPath();
		ctx.moveTo(pos.x + pos.halfWidth, pos.y + pos.wallHeight + padding);
		ctx.lineTo(pos.x + padding, pos.y + pos.wallHeight + pos.floorHalfHeight);
		ctx.lineTo(pos.x + pos.halfWidth, pos.y + pos.height - padding);
		ctx.lineTo(pos.x + pos.width - padding, pos.y + pos.wallHeight + pos.floorHalfHeight);
		ctx.closePath();
		ctx.stroke();
		return this;
	}

	/**
	 * Trigger change event
	 *
	 * @private
	 * @returns {void}
	 */
	_triggerChange() {
		this.trigger(EVENT_CHANGED);
	}
}
